import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { api } from '@/lib/api';
import { clearAnalyticsIdentity, track } from '@/lib/analytics';
import type { AuthUser } from '@/lib/types';
import { checkSessionWithDeadline } from '@/lib/session-check';
import { SessionCheckError } from '@/components/ui/session-check-error';
import { clearSavedSession } from '@/lib/session-recovery';
import { config } from '@/lib/config';
import { queryClient } from './query';

type Status = 'loading' | 'signed_in' | 'signed_out';

interface AuthContextValue {
  status: Status;
  user: AuthUser | null;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function toState(session: Session | null): { status: Status; user: AuthUser | null } {
  return session?.user
    ? { status: 'signed_in', user: api.auth.toAuthUser(session.user) }
    : { status: 'signed_out', user: null };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [failure, setFailure] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<{ status: Status; user: AuthUser | null }>({
    status: 'loading',
    user: null,
  });

  useEffect(() => {
    let active = true;
    let revision = 0;
    let validating = false;
    let deferred: ReturnType<typeof setTimeout>;
    const validate = async () => {
      if (!active || validating) return;
      validating = true;
      const request = ++revision;
      setFailure(false);
      try {
        const session = await checkSessionWithDeadline(() => api.auth.getSession());
        if (!active || request !== revision) return;
        if (!session) queryClient.clear();
        setState(toState(session));
      } catch {
        if (active && request === revision) setFailure(true);
      } finally {
        validating = false;
      }
    };

    void validate();

    const unsubscribe = api.auth.onAuthChange((event, session) => {
      if (!active || event === 'INITIAL_SESSION') return;
      if (event === 'PASSWORD_RECOVERY' && !window.location.pathname.startsWith('/reset-password')) {
        window.location.assign('/reset-password');
        return;
      }
      if (event === 'SIGNED_OUT') {
        clearAnalyticsIdentity();
        revision++;
        queryClient.clear();
        setState({ status: 'signed_out', user: null });
        return;
      }
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        // Supabase auth callbacks run under its session lock. Validate after it releases.
        clearTimeout(deferred);
        deferred = setTimeout(() => void validate(), 0);
        return;
      }
      setState((prev) => {
        const next = toState(session);
        // Token refreshes hand us a new user object with identical content —
        // keep the old reference so dependants don't re-render for nothing.
        if (
          prev.user &&
          next.user &&
          prev.user.id === next.user.id &&
          JSON.stringify(prev.user) === JSON.stringify(next.user)
        ) {
          return prev;
        }
        return next;
      });
    });
    window.addEventListener('focus', validate);

    return () => {
      active = false;
      clearTimeout(deferred);
      window.removeEventListener('focus', validate);
      unsubscribe();
    };
  }, [attempt]);

  const signOut = useCallback(async () => {
    try {
      await api.auth.signOut();
      track('logout');
      clearAnalyticsIdentity();
    } finally {
      queryClient.clear();
      setState({ status: 'signed_out', user: null });
    }
  }, []);

  const value = useMemo(() => ({ ...state, signOut }), [state, signOut]);
  return (
    <AuthContext.Provider value={value}>
      {state.status === 'loading' && failure ? (
        <SessionCheckError
          onReset={() => {
            clearSavedSession(config.supabaseUrl, [window.localStorage, window.sessionStorage]);
            queryClient.clear();
            window.location.replace('/login');
          }}
          onRetry={() => {
            setFailure(false);
            setAttempt((value) => value + 1);
          }}
        />
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
